import { intentsApi } from '@/api/intents'
import type { IntentColumn } from '@/components/projects/intents'
import {
  BulkDeleteIntentsDialog,
  DeleteIntentDialog,
  IntentDetailDialog,
  IntentsEmptyState,
  IntentsErrorState,
  IntentsFilters,
  IntentsHeader,
  IntentsLoadingState,
  IntentsSelectionBar,
  IntentsTable,
  EditIntentDialog,
} from '@/components/projects/intents'
import { TriggerWorkflowDialog } from '@/components/projects/workflows/TriggerWorkflowDialog'
import { TriggerWorkflowFromEntityDialog, type TriggerableDefinition } from '@/components/projects/workflows/TriggerWorkflowFromEntityDialog'
import { Pagination } from '@/components/ui'
import type { BulkDeleteIntentsResponse } from '@/hooks'
import { useAuth, useBulkDeleteIntents, useCsvExport, useDebounce, useIntent, useIntents, usePagination } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import type { Intent, IntentFilters as IntentFiltersType } from '@/types'
import { INTENT_DEFAULT_SORT_DIRECTIONS, type IntentSortColumn } from '@/types/intent'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

const FILTER_KEYS = ['search', 'epic', 'feature', 'status', 'sort', 'sortBy'] as const

export function IntentsPage() {
  const { projectId } = useParams()
  const { hasPermission, isLoading: authLoading } = useAuth(projectId)
  const [searchParams, setSearchParams] = useSearchParams()
  const { page, pageSize, paginationParams, setPage, setPageSize, resetPage } = usePagination()

  const storageKey = `intents-filters-${projectId}`

  const hasUrlFilters = FILTER_KEYS.some(k => searchParams.has(k))
  const sessionFilters = useMemo(
    () => new URLSearchParams(hasUrlFilters ? '' : (sessionStorage.getItem(storageKey) ?? '')),
    [hasUrlFilters, storageKey],
  )

  const search = searchParams.get('search') ?? sessionFilters.get('search') ?? ''
  const epicFilter = searchParams.get('epic') ?? sessionFilters.get('epic') ?? 'all'
  const featureFilter = searchParams.get('feature') ?? sessionFilters.get('feature') ?? 'all'
  const urlStatuses = searchParams.getAll('status')
  const statusKey = (urlStatuses.length > 0 ? urlStatuses : sessionFilters.getAll('status')).join('|')
  const statuses = useMemo(() => (statusKey ? statusKey.split('|') : []), [statusKey])
  const sortByParam = searchParams.get('sortBy') ?? sessionFilters.get('sortBy')
  const sortBy: IntentSortColumn =
    sortByParam && sortByParam in INTENT_DEFAULT_SORT_DIRECTIONS ? (sortByParam as IntentSortColumn) : 'number'
  const sortDirParam = searchParams.get('sort') ?? sessionFilters.get('sort')
  const sortDirection: 'asc' | 'desc' =
    sortDirParam === 'asc' || sortDirParam === 'desc' ? sortDirParam : INTENT_DEFAULT_SORT_DIRECTIONS[sortBy]

  const persistFilters = useCallback((params: URLSearchParams) => {
    const filterParams = new URLSearchParams()
    FILTER_KEYS.forEach(k => {
      params.getAll(k).forEach(v => {
        if (v) filterParams.append(k, v)
      })
    })
    sessionStorage.setItem(storageKey, filterParams.toString())
  }, [storageKey])

  const setFilter = useCallback(
    (key: string, value: string, isEmpty: (v: string) => boolean = v => !v || v === 'all') => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        if (isEmpty(value)) next.delete(key)
        else next.set(key, value)
        persistFilters(next)
        return next
      }, { replace: true })
    },
    [setSearchParams, persistFilters],
  )

  const handleStatusesChange = useCallback(
    (values: string[]) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        next.delete('status')
        values.forEach(s => next.append('status', s))
        persistFilters(next)
        return next
      }, { replace: true })
    },
    [setSearchParams, persistFilters],
  )

  const handleSortChange = (column: IntentSortColumn) => {
    const nextDirection =
      column === sortBy ? (sortDirection === 'asc' ? 'desc' : 'asc') : INTENT_DEFAULT_SORT_DIRECTIONS[column]
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('sortBy', column)
      next.set('sort', nextDirection)
      persistFilters(next)
      return next
    }, { replace: true })
    resetPage()
  }

  const didRestoreRef = useRef(false)
  useEffect(() => {
    if (didRestoreRef.current) return
    didRestoreRef.current = true
    if (FILTER_KEYS.some(k => searchParams.has(k))) return
    const saved = sessionStorage.getItem(storageKey)
    if (!saved) return
    const savedParams = new URLSearchParams(saved)
    if (!Array.from(savedParams.keys()).length) return
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      new Set(savedParams.keys()).forEach(key => {
        next.delete(key)
        savedParams.getAll(key).forEach(value => next.append(key, value))
      })
      return next
    }, { replace: true })
  }, [searchParams, setSearchParams, storageKey])

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showCheckboxes, setShowCheckboxes] = useState(false)
  const [visibleColumns, setVisibleColumns] = useState<IntentColumn[]>(['number', 'name', 'epic', 'feature', 'status', 'created'])
  const [editingIntentId, setEditingIntentId] = useState<string | null>(null)
  const [deletingIntent, setDeletingIntent] = useState<Intent | null>(null)
  const [triggeringIntentId, setTriggeringIntentId] = useState<string | null>(null)
  const [triggerPickerOpen, setTriggerPickerOpen] = useState(false)
  const [triggerDefinition, setTriggerDefinition] = useState<TriggerableDefinition | null>(null)
  const [bulkTriggerPickerOpen, setBulkTriggerPickerOpen] = useState(false)
  const [bulkTriggerDefinition, setBulkTriggerDefinition] = useState<TriggerableDefinition | null>(null)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)

  const debouncedSearch = useDebounce(search)

  const canEdit = hasPermission(Permissions.Project.Intent.Update, projectId!)
  const canDelete = hasPermission(Permissions.Project.Intent.Delete, projectId!)
  const canTrigger = hasPermission(Permissions.Project.Workflow.Trigger, projectId!)

  const bulkDeleteIntents = useBulkDeleteIntents(projectId!)
  const exportCsv = useCsvExport('intents')

  const handleBulkCancelSelection = useCallback(() => {
    setSelectedIds(new Set())
    setShowCheckboxes(false)
  }, [])

  const apiFilters: IntentFiltersType = useMemo(() => ({
    search: debouncedSearch || undefined,
    epic: epicFilter !== 'all' ? epicFilter : undefined,
    feature: featureFilter !== 'all' ? featureFilter : undefined,
    statuses: statuses.length > 0 ? statuses : undefined,
    sortBy,
    sortDirection,
  }), [debouncedSearch, epicFilter, featureFilter, statuses, sortBy, sortDirection])

  const { data, isLoading, isError, refetch } = useIntents(projectId!, paginationParams, apiFilters)
  const intents = useMemo(() => data?.items ?? [], [data])
  const totalCount = data?.totalCount ?? 0

  const handleBulkDeleteConfirm = useCallback(async () => {
    const ids = Array.from(selectedIds)
    let result: BulkDeleteIntentsResponse | undefined
    try {
      result = await bulkDeleteIntents.mutateAsync(ids)
    } catch {
      toast.error('Failed to delete intents')
      setBulkDeleteOpen(false)
      return
    }

    setBulkDeleteOpen(false)

    const deletedCount = result.deletedIds.length
    if (deletedCount > 0) {
      toast.success(`${deletedCount} ${deletedCount === 1 ? 'intent' : 'intents'} deleted successfully`)
    }

    result.skippedIds.forEach((skippedId, index) => {
      const intent = intents.find(d => d.id === skippedId)
      const label = intent ? `Intent #${String(intent.number).padStart(3, '0')}` : 'An intent'
      setTimeout(() => {
        toast.error(`${label} could not be deleted`)
      }, index * 300)
    })
  }, [selectedIds, bulkDeleteIntents, intents])

  const selectedIntents = useMemo(() => intents.filter(d => selectedIds.has(d.id)), [intents, selectedIds])
  const hasConflictingStatuses = useMemo(() => {
    const statuses = new Set(selectedIntents.map(d => d.status))
    return statuses.size > 1
  }, [selectedIntents])
  const selectedIntentTitles = useMemo(
    () => Object.fromEntries(selectedIntents.map(d => [d.id, d.name])),
    [selectedIntents],
  )
  const selectedIntentId = searchParams.get('intentId')
  const { data: selectedIntentDetail } = useIntent(projectId!, selectedIntentId)

  const selectedIntent = useMemo<Intent | null>(() => {
    const fromList = intents.find(d => d.id === selectedIntentId)
    if (fromList) return fromList
    if (!selectedIntentDetail) return null
    return {
      id: selectedIntentDetail.id,
      number: selectedIntentDetail.number,
      name: selectedIntentDetail.name,
      epic: selectedIntentDetail.epic,
      feature: selectedIntentDetail.feature,
      status: selectedIntentDetail.status,
      createdAt: selectedIntentDetail.createdAt,
    }
  }, [intents, selectedIntentId, selectedIntentDetail])

  useEffect(() => {
    resetPage()
  }, [debouncedSearch, epicFilter, featureFilter, statuses, resetPage])

  useEffect(() => {
    setSelectedIds(new Set())
  }, [data])

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleToggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      if (intents.length > 0 && intents.every(d => prev.has(d.id))) {
        return new Set()
      }
      return new Set(intents.map(d => d.id))
    })
  }, [intents])

  const handleToggleColumn = useCallback((column: IntentColumn) => {
    setVisibleColumns(prev =>
      prev.includes(column)
        ? prev.filter(c => c !== column)
        : [...prev, column]
    )
  }, [])

  const setSelectedIntentId = useCallback((intentId: string | null, options?: { replace?: boolean }) => {
    const next = new URLSearchParams(searchParams)
    if (intentId) next.set('intentId', intentId)
    else next.delete('intentId')
    setSearchParams(next, { replace: options?.replace ?? false })
  }, [searchParams, setSearchParams])

  if (authLoading) {
    return null
  }

  if (!hasPermission(Permissions.Project.Intent.Read, projectId!)) {
    return <Navigate to={`/projects/${projectId}/context`} replace />
  }

  const hasData = totalCount > 0
  const hasActiveFilters = search || epicFilter !== 'all' || featureFilter !== 'all' || statuses.length > 0

  const handleExportCsv = () => exportCsv.mutate({
    fetchCsv: () => intentsApi.exportCsv(projectId!, selectedIntents.map(w => w.id), visibleColumns),
    count: selectedIntents.length,
  })

  const handleExportAllCsv = () => exportCsv.mutate({
    fetchCsv: () => intentsApi.exportAllCsv(projectId!, apiFilters, visibleColumns),
    count: totalCount,
  })

  return (
    <div className="space-y-6">
      <IntentsHeader projectId={projectId!} onIntentCreated={resetPage} onExportCsvClick={handleExportAllCsv} exportDisabled={!hasData} />
      <IntentsFilters
        projectId={projectId!}
        search={search}
        onSearchChange={(v) => setFilter('search', v, s => !s)}
        epicFilter={epicFilter}
        onEpicFilterChange={(v) => setFilter('epic', v)}
        featureFilter={featureFilter}
        onFeatureFilterChange={(v) => setFilter('feature', v)}
        statuses={statuses}
        onStatusesChange={handleStatusesChange}
        showCheckboxes={showCheckboxes}
        onToggleCheckboxes={() => setShowCheckboxes(prev => !prev)}
        visibleColumns={visibleColumns}
        onToggleColumn={handleToggleColumn}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onClearAll={() => {
          setSearchParams(prev => {
            const next = new URLSearchParams(prev)
            FILTER_KEYS.forEach(k => next.delete(k))
            persistFilters(next)
            return next
          }, { replace: true })
          resetPage()
        }}
      />

      {isLoading ? (
        <IntentsLoadingState />
      ) : isError ? (
        <IntentsErrorState onRetry={() => refetch()} />
      ) : hasData || hasActiveFilters ? (
        <div className="rounded-2xl border overflow-hidden">
          <IntentsTable
            intents={intents}
            showCheckboxes={showCheckboxes}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
            visibleColumns={visibleColumns}
            canDelete={canDelete}
            canTrigger={canTrigger}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            onSelect={(intent) => setSelectedIntentId(intent.id)}
            onDelete={setDeletingIntent}
            onTrigger={(intent) => { setTriggeringIntentId(intent.id); setTriggerPickerOpen(true) }}
          />
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      ) : (
        <IntentsEmptyState />
      )}

      <IntentDetailDialog
        intent={selectedIntent}
        projectId={projectId!}
        open={selectedIntent !== null}
        onOpenChange={(open) => { if (!open) setSelectedIntentId(null, { replace: true }) }}
        onEdit={canEdit ? () => {
          if (selectedIntent) {
            setEditingIntentId(selectedIntent.id)
            setSelectedIntentId(null, { replace: true })
          }
        } : undefined}
      />

      {editingIntentId && (
        <EditIntentDialog
          projectId={projectId!}
          intentId={editingIntentId}
          open
          onOpenChange={(open: boolean) => { if (!open) setEditingIntentId(null) }}
        />
      )}

      {deletingIntent && (
        <DeleteIntentDialog
          projectId={projectId!}
          intent={deletingIntent}
          open
          onOpenChange={(open: boolean) => { if (!open) setDeletingIntent(null) }}
        />
      )}

      <IntentsSelectionBar
        selectedCount={selectedIds.size}
        onTrigger={() => setBulkTriggerPickerOpen(true)}
        triggerDisabled={hasConflictingStatuses}
        triggerDisabledReason={hasConflictingStatuses ? 'Select intents with the same status.' : undefined}
        canTrigger={canTrigger}
        onDelete={() => setBulkDeleteOpen(true)}
        onExport={handleExportCsv}
        canDelete={canDelete}
        onCancel={handleBulkCancelSelection}
      />

      <BulkDeleteIntentsDialog
        intents={selectedIntents}
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        onConfirm={handleBulkDeleteConfirm}
        isPending={bulkDeleteIntents.isPending}
      />

      <TriggerWorkflowFromEntityDialog
        projectId={projectId!}
        open={triggerPickerOpen}
        onOpenChange={setTriggerPickerOpen}
        onSelect={(def) => setTriggerDefinition(def)}
        linkedEntityType="Intent"
        linkedEntityId={triggeringIntentId ?? undefined}
      />

      <TriggerWorkflowDialog
        projectId={projectId!}
        definition={triggerDefinition}
        open={triggerDefinition !== null}
        onOpenChange={(open) => { if (!open) setTriggerDefinition(null) }}
        extraInputValues={triggeringIntentId ? { workItemId: triggeringIntentId } : undefined}
        linkedEntityId={triggeringIntentId ?? undefined}
      />

      <TriggerWorkflowFromEntityDialog
        projectId={projectId!}
        open={bulkTriggerPickerOpen}
        onOpenChange={setBulkTriggerPickerOpen}
        onSelect={(def) => setBulkTriggerDefinition(def)}
        linkedEntityType="Intent"
        linkedEntityId={selectedIntents[0]?.id ?? undefined}
      />

      <TriggerWorkflowDialog
        projectId={projectId!}
        definition={bulkTriggerDefinition}
        open={bulkTriggerDefinition !== null}
        onOpenChange={(open) => { if (!open) setBulkTriggerDefinition(null) }}
        extraInputValues={{ workItemId: '__bulk__' }}
        extraInputValuesPerEntity={(id) => ({ workItemId: id })}
        linkedEntityIds={Array.from(selectedIds)}
        entityTitles={selectedIntentTitles}
        onTriggerSuccess={handleBulkCancelSelection}
      />
    </div>
  )
}
